import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import { api } from "../../lib/api";

interface Illustration {
  id: string;
  storage_path: string;
  title: string;
}

type WorksGalleryProps = {
  worksId: number;
  isVisible: boolean;
};

export const WorksGallery = ({ worksId, isVisible }: WorksGalleryProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<Illustration | null>(null);
  const [visibleIllustrations, setVisibleIllustrations] = useState<
    Illustration[]
  >([]);
  const [thumbnailUrls, setThumbnailUrls] = useState<{ [key: string]: string }>(
    {}
  );
  const [isLoading, setIsLoading] = useState(false);
  const [countImages, setCountImages] = useState(0);

  const preventImageDownload = (e: React.MouseEvent) => e.preventDefault();

  useEffect(() => {
    const fetchIllustrations = async () => {
      if (!worksId) return;
      setIsLoading(true);

      try {
        // カテゴリー（worksId）に基づいたイラストのみを取得
        const data = await api.getWorksIllustrations(worksId);
        
        if (!data || data.length === 0) {
          setVisibleIllustrations([]);
          setIsLoading(false);
          return;
        }

        setCountImages(data.length);
        setVisibleIllustrations([]);

        // サムネイルを順次表示
        const fetchAllThumbnails = async () => {
          for (let i = 0; i < data.length; i += 5) {
            const batch = data.slice(i, i + 5);
            batch.forEach((item: any) => {
              const url = api.getImageUrl(item.storage_path, { width: 500 });
              setThumbnailUrls(prev => ({ ...prev, [item.id]: url }));
            });
            
            setVisibleIllustrations((prev) => [...prev, ...batch]);
            
            if (i + 5 < data.length) {
              await new Promise((resolve) => setTimeout(resolve, 300));
            }
          }
          setIsLoading(false);
        };
        fetchAllThumbnails();

      } catch (error) {
        console.error("Error fetching illustrations:", error);
        setIsLoading(false);
      }
    };

    if (isVisible) {
      fetchIllustrations();
    }
  }, [isVisible, worksId]);

  const getImageUrl = (id: string, isFullSize: boolean = false) => {
    const illustration = visibleIllustrations.find(img => img.id === id) || 
                         (selectedImage?.id === id ? selectedImage : null);
    
    if (!illustration) return undefined;

    // ポップアップ時のみ透かしを入れる
    return api.getImageUrl(illustration.storage_path, { 
      width: isFullSize ? undefined : 500,
      watermark: isFullSize 
    });
  };

  if (!isVisible) return null;

  return (
    <>
      {countImages > 0 && isLoading && (
        <p className="mt-4 text-lg font-semibold">{`${countImages} 枚あります...`}</p>
      )}

      {isLoading && visibleIllustrations.length === 0 ? (
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-gray-900"></div>
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="grid grid-cols-2 md:grid-cols-3 gap-4 w-full max-w-4xl mx-auto mt-8"
        >
          {visibleIllustrations.length > 0 ? (
            visibleIllustrations.map((illustration, index) => (
              <motion.div
                key={`${illustration.id}-${index}`}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
                whileHover={{ scale: 1.05 }}
                className="aspect-square rounded-lg shadow-md cursor-pointer overflow-hidden relative"
                onClick={() => {
                  setSelectedImage(illustration);
                  setIsModalOpen(true);
                }}
              >
                {thumbnailUrls[illustration.id] ? (
                  <img
                    src={thumbnailUrls[illustration.id]}
                    className="w-full h-full object-cover select-none pointer-events-none"
                    onContextMenu={preventImageDownload}
                    onDragStart={preventImageDownload}
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-900"></div>
                  </div>
                )}
              </motion.div>
            ))
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="col-span-full text-center py-12"
            >
              <p className="text-2xl font-semibold text-gray-600">
                Coming Soon
              </p>
            </motion.div>
          )}
        </motion.div>
      )}

      <AnimatePresence>
        {isModalOpen && selectedImage && (
          <div
            className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
            onClick={() => setIsModalOpen(false)}
          >
            <div className="w-full h-full flex items-center justify-center">
              <img
                src={getImageUrl(selectedImage.id, true)}
                className="max-w-full max-h-[90vh] object-contain select-none cursor-pointer"
                onContextMenu={preventImageDownload}
                onDragStart={preventImageDownload}
                onClick={() => setIsModalOpen(false)}
              />
            </div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};
